import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { TOUR_FEATURE_MAP } from '../constants/tourGuide';
import { useTour } from '../components/TourProvider';

const API_BASE_URL = (process.env.REACT_APP_BASE_URL || "http://localhost:8000/api/v1") + "/chatbot";

export const useTourGuide = () => {
  const location = useLocation();
  const { showFeatureTour } = useTour();
  const { userData } = useSelector((store) => store.auth);
  const userId = userData?.id?.toString() || 'default';
  const [promptsDisabled, setPromptsDisabled] = useState(null);
  const [visitedRoutes, setVisitedRoutes] = useState(null); // null = not yet loaded

  // Fetch prompt disable status and visited routes on mount
  useEffect(() => {
    if (!userId || userId === 'default') {
      setPromptsDisabled(false);
      setVisitedRoutes([]);
      return;
    }

    // Fetch prompt disable status
    fetch(`${API_BASE_URL}/prompts/${userId}/status`)
      .then((res) => res.json())
      .then((data) => {
        setPromptsDisabled(data.prompts_disabled || false);
      })
      .catch(() => {
        setPromptsDisabled(false);
      });

    // Fetch visited tour routes
    fetch(`${API_BASE_URL}/tour/${userId}/visited`)
      .then((res) => res.json())
      .then((data) => {
        setVisitedRoutes(data.visited_routes || []);
      })
      .catch(() => {
        setVisitedRoutes([]);
      });
  }, [userId]);

  useEffect(() => {
    // Wait until both promptsDisabled and visitedRoutes are loaded
    if (promptsDisabled === null || visitedRoutes === null) return;

    const currentPath = location.pathname;

    // Find matching feature by prefix
    let featureName = null;
    let matchedPrefix = null;
    const sortedPrefixes = Object.keys(TOUR_FEATURE_MAP).sort((a, b) => b.length - a.length);
    for (const prefix of sortedPrefixes) {
      if (currentPath === prefix || currentPath.startsWith(prefix + '/')) {
        featureName = TOUR_FEATURE_MAP[prefix];
        matchedPrefix = prefix;
        break;
      }
    }

    if (!featureName || !matchedPrefix) return;
    if (promptsDisabled) return;
    if (visitedRoutes.includes(matchedPrefix)) return;

    // Mark route as visited on backend, then show tour
    setTimeout(() => {
      showFeatureTour(featureName);

      // Update backend
      fetch(`${API_BASE_URL}/tour/${userId}/visited`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ route: matchedPrefix }),
      }).catch((err) => {
        console.error('[TOUR] Failed to mark route visited:', err);
      });

      // Update local state to prevent re-triggering
      setVisitedRoutes((prev) => [...prev, matchedPrefix]);
    }, 1500);
  }, [location.pathname, showFeatureTour, userId, promptsDisabled, visitedRoutes]);
};
